/**
 * Anonymous wishlist.
 *
 * Product ids in `localStorage`, nothing else. No account, no app, no request
 * to anyone: the list never leaves the browser, which is why it needs no
 * consent, and why it survives a reload without a login.
 *
 * The buttons render hidden and this module reveals them, so a visitor never
 * sees a control that cannot work. Everything else on the page is unaffected
 * if this file fails to load.
 */
import { animate, prefersReducedMotion } from 'theme:motion';

const STORAGE_KEY = 'sofia:wishlist';

/**
 * Exported so the wishlist page reads the list through this module rather than
 * copying the storage key into a second file, which is how the two drift apart
 * the day the key changes.
 */
export function read() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.map(String) : [];
  } catch {
    return [];
  }
}

function write(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Private browsing and full quotas both throw. Losing a wishlist is
    // acceptable; breaking the page the visitor is reading is not.
  }
  document.dispatchEvent(new CustomEvent('theme:wishlist:change', { detail: { ids } }));
}

let accountReady = false;
let syncTimer = null;

async function saveAccount(ids) {
  if (!strings.signedIn || !accountReady) return;
  try {
    const response = await fetch(strings.syncUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!response.ok) return;
    const payload = await response.json();
    const merged = Array.isArray(payload.ids) ? payload.ids.map(String) : ids;
    if (JSON.stringify(merged) !== JSON.stringify(read())) write(merged);
  } catch {
    // The local wishlist remains authoritative while the Portal is unavailable.
  }
}

function queueAccountSave(ids) {
  if (!strings.signedIn || !accountReady) return;
  window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(() => saveAccount(ids), 350);
}

async function mergeAccount() {
  if (!strings.signedIn) return;
  try {
    const response = await fetch(strings.syncUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const payload = await response.json();
    const remote = Array.isArray(payload.ids) ? payload.ids.map(String) : [];
    const merged = [...new Set([...read(), ...remote])];
    accountReady = true;
    write(merged);
    if (merged.length !== remote.length) await saveAccount(merged);
  } catch {
    // Anonymous storage continues to work and will merge on a later visit.
  }
}

export function has(id) {
  return read().includes(String(id));
}

export function toggle(id) {
  const ids = read();
  const key = String(id);
  const next = ids.includes(key) ? ids.filter((entry) => entry !== key) : [key, ...ids];
  write(next);
  return next.includes(key);
}

/* ---------------------------------------------------------------------------
   Buttons
   --------------------------------------------------------------------------- */

const strings = JSON.parse(
  document.querySelector('[data-wishlist-strings]')?.textContent ?? '{}'
);

function paint(button) {
  const id = button.dataset.wishlistToggle;
  const active = has(id);

  button.setAttribute('aria-pressed', String(active));
  button.hidden = false;

  const label = button.querySelector('[data-wishlist-label]');
  if (label) label.textContent = active ? strings.remove : strings.add;
}

function paintAll(root = document) {
  root.querySelectorAll('[data-wishlist-toggle]').forEach(paint);
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-wishlist-toggle]');
  if (!button) return;

  event.preventDefault();
  // A wishlist button inside a product card sits within a link to the product.
  event.stopPropagation();

  const added = toggle(button.dataset.wishlistToggle);
  queueAccountSave(read());
  paintAll();

  if (added && !prefersReducedMotion()) {
    animate(
      button.querySelector('svg'),
      { transform: ['scale(1)', 'scale(1.25)', 'scale(1)'] },
      { duration: 0.34, easing: [0.16, 1, 0.3, 1] }
    );
  }
});

/* ---------------------------------------------------------------------------
   Header counter
   --------------------------------------------------------------------------- */

function paintCount() {
  const count = read().length;
  document.querySelectorAll('[data-wishlist-count]').forEach((node) => {
    node.textContent = String(count);
    node.hidden = count === 0;
  });
  document.querySelectorAll('[data-wishlist-link]').forEach((node) => {
    const template = node.dataset.wishlistLabel;
    if (template) node.setAttribute('aria-label', template.replace('%count%', count));
  });
}

document.addEventListener('theme:wishlist:change', () => paintCount());

/**
 * Another tab changed the list.
 *
 * `storage` fires in every OTHER tab, never in the one that wrote, so this
 * cannot loop. Without it a visitor with two tabs open sees one counter say
 * three and the other say two, and neither is obviously wrong.
 *
 * The synthetic event is dispatched so the wishlist page repaints too, and it
 * carries the same shape as the local one.
 */
window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY) return;
  paintAll();
  paintCount();
  document.dispatchEvent(
    new CustomEvent('theme:wishlist:change', { detail: { ids: read(), remote: true } })
  );
});

paintAll();
paintCount();
mergeAccount();

// Shelves and grids that arrive after load carry their own buttons.
document.addEventListener('theme:carousel:refresh', (event) => {
  if (event.detail?.root) paintAll(event.detail.root);
});
